import React from 'react';
import { Box, Image } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { SHARE_VIDEOS } from 'services/shared/imageService';

const MotionBox = motion.create(Box);

export interface HeroVisualItem {
	type: 'video' | 'image';
	src: string;
	x: number;
	y: number;
	rotate: number;
	delay: number;
	hideLg?: boolean;
	width: string;
	height: string;
}

interface HeroVisualProps {
	items?: HeroVisualItem[];
	centerSrc?: string;
}

const DEFAULT_CENTER = SHARE_VIDEOS.vmp4_238b20ff_eae0_44a1_9ec7_edb208aee28a_hd;

export const DEFAULT_HERO_VISUAL_ITEMS: HeroVisualItem[] = [
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_0319bbf2_a06b_4664_b094_0dfcd5ea0b51,
		x: 280,
		y: -180,
		rotate: 12,
		delay: 0.2,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_0d83d5f4_f027_4461_bd4d_711befc65285_hd,
		x: -280,
		y: -200,
		rotate: -15,
		delay: 0.1,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_118867b0_86eb_4b9e_837b_9fcb62d44e13_hd,
		x: -200,
		y: 120,
		rotate: -10,
		delay: 0.3,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_1583324e_50b1_498e_a1ec_e39acf81d36b,
		x: -320,
		y: -40,
		rotate: -8,
		delay: 0.7,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_17ce1875_3d2a_4850_9f59_e5dcb8a2cff9_hd,
		x: 360,
		y: 180,
		rotate: 8,
		delay: 0.4,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_19a52e15_e11c_41a2_89ae_6a7949390ff3_hd,
		x: 80,
		y: -280,
		rotate: 5,
		delay: 0.5,
		hideLg: true,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_1b138564_702e_4457_97fc_42d00525a76a_hd,
		x: 100,
		y: 280,
		rotate: -5,
		delay: 0.6,
		width: '120px',
		height: '160px',
	},
	{
		type: 'video',
		src: SHARE_VIDEOS.vmp4_1f7e1655_943a_436a_a4fb_076996614cfb_hd,
		x: 300,
		y: 40,
		rotate: 10,
		delay: 0.8,
		width: '120px',
		height: '160px',
	},
];

const ShineEffect = () => (
	<MotionBox
		position="absolute"
		top={0}
		left="-100%"
		w="100%"
		h="100%"
		background="linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)"
		transform="skewX(-20deg)"
		animate={{
			left: ['-100%', '200%'],
		}}
		transition={{
			duration: 2.5,
			repeat: Infinity,
			repeatDelay: 3,
			ease: 'easeInOut',
		}}
		zIndex={5}
		pointerEvents="none"
	/>
);

const HeroVisual = ({ items = DEFAULT_HERO_VISUAL_ITEMS, centerSrc = DEFAULT_CENTER }: HeroVisualProps) => {
	return (
		<Box
			position="relative"
			h={{ base: '400px', lg: '600px' }}
			w="full"
			display="flex"
			alignItems="center"
			justifyContent="center"
		>
			{/* Ambient glow */}
			<Box
				position="absolute"
				inset="-40px"
				zIndex={-1}
				borderRadius="3rem"
				bg="primary"
				opacity={0.12}
				filter="blur(60px)"
				pointerEvents="none"
			/>

			{/* Central Main Item */}
			<MotionBox
				initial={{ scale: 0.8, opacity: 0 }}
				animate={{
					scale: 1,
					opacity: 1,
					y: [0, -10, 0],
				}}
				transition={{
					scale: { duration: 0.8, ease: 'easeOut' },
					opacity: { duration: 0.8, ease: 'easeOut' },
					y: {
						duration: 4,
						repeat: Infinity,
						ease: 'easeInOut',
						delay: 0.8,
					},
				}}
				position="relative"
				zIndex={10}
				w="240px"
				h="320px"
				borderRadius="24px"
				shadow="2xl"
				overflow="hidden"
			>
				<ShineEffect />
				<Box position="relative" w="full" h="full">
					<video
						autoPlay
						loop
						muted
						playsInline
						src={centerSrc}
						style={{ objectFit: 'cover', width: '100%', height: '100%' }}
					/>
				</Box>
			</MotionBox>

			{/* Spread Items */}
			{items.map((item, idx) => (
				<MotionBox
					key={idx}
					initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0.5 }}
					animate={{
						x: item.x,
						y: [item.y, item.y - 10, item.y],
						opacity: 1,
						rotate: item.rotate,
						scale: 1,
					}}
					transition={{
						x: { type: 'spring', stiffness: 50, damping: 15, delay: item.delay },
						opacity: { duration: 0.5, delay: item.delay },
						rotate: { duration: 0.5, delay: item.delay },
						scale: { duration: 0.5, delay: item.delay },
						y: {
							duration: 4,
							repeat: Infinity,
							ease: 'easeInOut',
							delay: item.delay + 0.8,
							times: [0, 0.5, 1],
						},
					}}
					position="absolute"
					zIndex={idx}
					w={item.width}
					h={item.height}
					borderRadius="16px"
					shadow="xl"
					overflow="hidden"
					display={{ base: item.hideLg ? 'none' : 'block', lg: 'block' }}
				>
					<ShineEffect />
					<Box position="relative" w="full" h="full">
						{item.type === 'image' ? (
							<Image
								src={item.src}
								alt="hero visual"
								style={{ objectFit: 'cover', width: '100%', height: '100%' }}
							/>
						) : (
							<video
								autoPlay
								loop
								muted
								playsInline
								src={item.src}
								style={{ objectFit: 'cover', width: '100%', height: '100%' }}
							/>
						)}
					</Box>
				</MotionBox>
			))}
		</Box>
	);
};

export default HeroVisual;
